import { useState, useCallback, useEffect, useRef } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useTranslation } from "react-i18next";
import { Input } from "@shadcn-in-nx/ui/input";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PlanRestrictionAlert from "../Chat/ChatTwoColumnPage/PlanRestrictionAlert";
import SecurityOverviewCards from "./SecurityOverviewCards";
import SecurityProjectsTable from "./SecurityProjectsTable";
import SecurityTemplateConfig, { PRESETS } from "./SecurityTemplateConfig";
import SecurityEmptyState from "./SecurityEmptyState";
import SecuritySummaryCards from "./SecuritySummaryCards";

const Security = () => {
    const { t } = useTranslation();
    const { isFeatureEnabled, getCustomPlanNumberFeature } = useAuth();
    const hasAccess = isFeatureEnabled("codeScanning");
    const codeScanMaxRules = getCustomPlanNumberFeature("codeScanMaxRules");
    const [selectedTemplate, setSelectedTemplate] = useState("standard");
    const [enabledRules, setEnabledRules] = useState(PRESETS.standard);
    const [searchFilter, setSearchFilter] = useState("");
    const [page, setPage] = useState(1);
    const [scanning, setScanning] = useState(null);
    const loadedSavedTemplate = useRef(false);

    const { response, isLoading, call: fetchDashboard } = useBluelagoon({
        method: "getSecurityDashboard",
        resetResponse: false,
    });

    const { call: saveTemplate } = useBluelagoon({ method: "saveSecurityTemplate" });
    const { call: triggerScan } = useBluelagoon({ method: "triggerCodeScan" });

    const refresh = useCallback((p, s) => {
        const params = { page: p, pageSize: 25 };
        if (s) params.search = s;
        fetchDashboard(params);
    }, [fetchDashboard]);

    useEffect(() => {
        if (hasAccess) refresh(page, searchFilter);
    }, [hasAccess, page]);

    useEffect(() => {
        if (!hasAccess) return;
        setPage(1);
        refresh(1, searchFilter);
    }, [searchFilter]);

    // Load saved template from response
    const data = response?.data;
    useEffect(() => {
        if (data?.savedTemplate && !loadedSavedTemplate.current) {
            loadedSavedTemplate.current = true;
            setSelectedTemplate(data.savedTemplate.templateId || "standard");
            setEnabledRules(data.savedTemplate.enabledRules || PRESETS.standard);
        }
    }, [data?.savedTemplate]);

    const [isDirty, setIsDirty] = useState(false);

    const handleTemplateChange = useCallback((templateId) => {
        setSelectedTemplate(templateId);
        if (PRESETS[templateId]) setEnabledRules(PRESETS[templateId]);
        setIsDirty(true);
    }, []);

    const handleRulesChange = useCallback((rules) => {
        setEnabledRules(rules);
        setSelectedTemplate("custom");
        setIsDirty(true);
    }, []);

    const handleSaveTemplate = useCallback(() => {
        saveTemplate({ templateId: selectedTemplate, enabledRules });
        setIsDirty(false);
    }, [saveTemplate, selectedTemplate, enabledRules]);

    const handleScan = useCallback(async (stepId) => {
        setScanning(stepId);
        try {
            await triggerScan({
                stepId,
                templateId: selectedTemplate === "custom" ? "custom" : selectedTemplate,
                customRules: selectedTemplate === "custom" ? enabledRules : undefined,
            });
            toast.success(t("appPreview.scanStarted"));
            setTimeout(() => {
                refresh(page, searchFilter);
                setScanning(null);
            }, 3000);
        } catch (err) {
            const msg = err?.message || err?.error?.message || err?.error || t("appPreview.scanFailed");
            toast.error(typeof msg === "string" ? msg : t("appPreview.scanFailed"));
            setScanning(null);
        }
    }, [triggerScan, selectedTemplate, enabledRules, refresh, page, searchFilter, t]);

    const hasScans = data?.summary?.scannedCount > 0;
    const pagination = data?.pagination;

    if (!hasAccess) {
        return (
            <div className="space-y-6">
                <h2 className="text-xl font-semibold">{t("security.title")}</h2>
                <PlanRestrictionAlert
                    titleKey="security.upgradeRequired"
                    titleFallback="Upgrade Required"
                    descriptionKey="security.upgradeMessage"
                    descriptionFallback="The Security Dashboard is available on the Visionary plan ($250/mo) and above."
                    variant="inline"
                />
            </div>
        );
    }

    if (isLoading && !data) {
        return (
            <div className="space-y-4">
                <h2 className="text-xl font-semibold">{t("security.title")}</h2>
                <p className="text-muted-foreground">{t("security.loading")}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold">{t("security.title")}</h2>
            <SecuritySummaryCards />
            {hasScans ? (
                <SecurityOverviewCards summary={data.summary} projects={data?.projects || []} />
            ) : (
                <SecurityEmptyState />
            )}
            <SecurityTemplateConfig
                selectedTemplate={selectedTemplate}
                onTemplateChange={handleTemplateChange}
                enabledRules={enabledRules}
                onRulesChange={handleRulesChange}
                isDirty={isDirty}
                onSave={handleSaveTemplate}
                maxRules={codeScanMaxRules}
            />
            <div className="flex items-center">
                <div className="relative ml-auto">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder={t("security.searchPlaceholder")}
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        className="pl-9 w-64"
                    />
                </div>
            </div>
            <SecurityProjectsTable
                projects={data?.projects || []}
                onScan={handleScan}
                scanning={scanning}
                page={pagination?.page || 1}
                totalPages={pagination?.totalPages || 1}
                totalCount={pagination?.totalCount || 0}
                onPageChange={setPage}
            />
        </div>
    );
};

export default Security;
